import { PortalDialogProps } from "@app/services/modal.service";
import { CreateUser } from "@app/types";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Modal from "../modal/modal.component";
import {
  MAX_SIZE_UPLOAD,
  Role,
  SystemMessage,
  RoleOptions,
} from "@app/constants";
import { FormControl } from "../form-control";
import Input from "../input";
import Button from "../button";
import Checkbox from "../checkbox";
import { Images } from "@assets/images";
import { createUserValidationSchema } from "@app/validations";
import { CustomSelect } from "../select/custom-select";
import ChevronDownArrow from "../select/chevron-down";

interface CreateUserProps {
  user: CreateUser;
}

const CreateUserModal = ({
  portalDialogRef,
  portalData,
}: PortalDialogProps<CreateUserProps>) => {
  const formRef = useRef<FormikContextType<CreateUser>>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [showMaxVoiceTrain, setShowMaxVoiceTrain] = useState(false);
  const [isSelectedRoleAuthor, setIsSelectedRoleAuthor] = useState(false);

  const onSubmit = (values: CreateUser) => {
    if (!formRef.current) return;

    let user: CreateUser = {
      fullName: values.fullName,
      username: values.username,
      password: values.password,
      role: values.role,
    };

    if (values.role === Role.AUTHOR) {
      user = {
        ...user,
        maxVoiceTrain: values.maxVoiceTrain || 0,
        canCreateAudio: Boolean(values.canCreateAudio),
        canUploadAudio: Boolean(values.canUploadAudio),
      };
    } else {
      user = { ...user, phone: values.phone, email: values.email };
    }

    if (inputFileRef.current?.files?.length) {
      const file = inputFileRef.current.files[0];

      if (file.size > MAX_SIZE_UPLOAD) {
        formRef.current.setFieldError(
          "filename",
          SystemMessage.MAX_SIZE_UPLOAD
        );

        return;
      }

      user = { ...user, thumbnail: file };
    }

    portalDialogRef.close({ user });
  };

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    const file = event.target.files?.[0];

    if (file) {
      if (file.size > MAX_SIZE_UPLOAD) {
        formRef.current.setFieldError(
          "filename",
          SystemMessage.MAX_SIZE_UPLOAD
        );
        formRef.current.setFieldValue("filename", "");

        return;
      }

      formRef.current.setFieldError("filename", undefined);
      formRef.current.setFieldValue("filename", file.name);
    } else {
      formRef.current.setFieldValue("filename", "");
    }
  };

  useEffect(() => {
    if (portalData?.user && formRef.current) {
      formRef.current.setValues({
        thumbnail: undefined,
        fullName: portalData.user.fullName,
        username: portalData.user.username,
        password: portalData.user.password,
        phone: portalData.user.phone,
        email: portalData.user.email,
        role: portalData.user.role,
        canCreateAudio: Boolean(portalData.user.canCreateAudio),
        canUploadAudio: Boolean(portalData.user.canUploadAudio),
      });
      setShowMaxVoiceTrain(portalData.user.role === Role.AUTHOR);
    }
  }, [portalData?.user]);

  const handleRoleOptionChange = (e: number | string) => {
    if (formRef.current) {
      formRef.current.setFieldValue("role", e);

      if (e === Role.AUTHOR) {
        setShowMaxVoiceTrain(true);
        formRef.current.setFieldValue("maxVoiceTrain", 0);
        formRef.current.setFieldValue("canCreateAudio", false);
        formRef.current.setFieldValue("canUploadAudio", false);
      } else {
        setShowMaxVoiceTrain(false);
        formRef.current.setFieldValue("maxVoiceTrain", undefined);
        formRef.current.setFieldValue("canCreateAudio", false);
        formRef.current.setFieldValue("canUploadAudio", false);
      }
    }

    setIsSelectedRoleAuthor(e === Role.AUTHOR ? true : false);
  };

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">Tạo User/Author</div>
        <Formik
          innerRef={formRef}
          initialValues={{
            filename: "",
            username: "",
            password: "",
            fullName: "",
            phone: "",
            email: "",
            role: Role.USER,
            maxVoiceTrain: 0,
            canCreateAudio: false,
            canUploadAudio: false,
          }}
          onSubmit={onSubmit}
          validationSchema={createUserValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File ảnh"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <FormControl name="fullName">
              <Input className="mt-3" placeholder="Họ và tên" />
            </FormControl>
            <FormControl name="username">
              <Input className="mt-3" placeholder="Tên đăng nhập" />
            </FormControl>
            <FormControl name="password">
              <Input className="mt-3" placeholder="Mật khẩu" type="password" />
            </FormControl>
            {!isSelectedRoleAuthor && (
              <>
                <FormControl name="email">
                  <Input className="mt-3" placeholder="Email" />
                </FormControl>
                <FormControl name="phone">
                  <Input className="mt-3" placeholder="Phone" />
                </FormControl>
              </>
            )}
            <FormControl name="role">
              <CustomSelect
                options={RoleOptions}
                className="bg-color w-full !h-[52px] px-1 mt-3 rounded-lg pr-4"
                optionContainerClassName="mt-4 rounded-b-lg w-full overflow-y-auto w-36"
                optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                labelSelectedClassName="black-50 !mx-3"
                placeholder="Role"
                arrow={<ChevronDownArrow />}
                hasBgColorHover
                onChange={handleRoleOptionChange}
              />
            </FormControl>

            {showMaxVoiceTrain && (
              <>
                <FormControl name="maxVoiceTrain">
                  <Input
                    className="mt-3"
                    placeholder="Max Voice Train"
                    type="number"
                  />
                </FormControl>
                <div className="mt-3">
                  <FormControl name="canCreateAudio">
                    <Checkbox
                      label="Cho phép author tạo audio AI từ văn bản"
                      labelClassName="text-sm text-[#272C36]"
                    />
                  </FormControl>
                </div>
                <div className="mt-3">
                  <FormControl name="canUploadAudio">
                    <Checkbox
                      label="Cho phép author upload audio có sẵn"
                      labelClassName="text-sm text-[#272C36]"
                    />
                  </FormControl>
                </div>
              </>
            )}
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label="Tạo"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
};

export default CreateUserModal;
